import { useContext } from 'react';

import { HubContext } from '../../utils';

/**
 * Whether auto-derived readable text colours are active for this render.
 *
 * Scoped to JSM Help Hubs deliberately:
 *
 *   • **Confluence Company Hub** renders these extensions without mounting
 *     `HubContext`, so a missing context returns `false` — the same
 *     cross-host-safety contract `useIsAdvancedThemingEnabled` relies on.
 *   • **Customer Service (CSM) hubs** do mount the context, so they need the
 *     explicit `hubType` check: they have their own colour defaults
 *     (`CustomerServiceHelpCenterDefaults`) and a white brand colour, and are
 *     not part of the advanced-theming rollout. `useIsAdvancedThemingEnabled`
 *     only gates on a product flag whose HELP_HUB scoping is the product's
 *     responsibility, so relying on it alone would leave CSM reachable if that
 *     flag were ever set for another surface.
 *
 * Mirrors `useShouldShowPremiumUpsell`'s hub-type guard so the whole theming
 * family agrees on what "a help hub" means.
 */
export const useIsHubAutoTextColorEnabled = (): boolean => {
	const ctx = useContext(HubContext);

	if (!ctx) {
		// No HubContextProvider in tree (e.g. Confluence Company Hub).
		return false;
	}

	if (ctx.hubType !== 'HELP_HUB') {
		// Customer Service hubs keep their existing colours untouched.
		return false;
	}

	return true;
};
